/* eslint-disable no-unused-expressions */
/* eslint-disable react/destructuring-assignment */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, makeStyles, Modal } from '@material-ui/core';
import clsx from 'clsx';
import { readExcel } from '../../utils/utils';
import BackButton from '../BackButton/BackButton';
import CsvPreviewTable from '../CsvPreviewTable/CsvPreviewTable';
import AddNewRowToCsvPreviewForm from '../AddNewRowToCsvPreviewForm/AddNewRowToCsvPreviewForm';
import styles from './CsvPreview.module.css';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { ACTIONS, MESSAGE } from '../../constants';
import InfoIconWithPopover from '../InfoIconWithPopover/InfoIconWithPopover';

const useStyles = makeStyles(theme => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
}));

const CsvPreview = ({
  open,
  setIsCsvPreviewOpen,
  csvFileData,
  setCsvFileData,
  backButtonName,
  formName,
  csvDataFormatter,
  handleSubmit,
  headerConfiguration,
  headersWithJsonFields,
  requiredParams,
  validCsvHeaders,
  addNewRowFormName,
  editRowFormName,
  addNewRowSuccessMessage,
  editRowSuccessMessage,
}) => {
  const classes = useStyles();
  const ROW_HEIGHT = 38;
  const { showError } = usePopupManager() || true;
  // eslint-disable-next-line no-unused-vars
  const [hasActiveEdits, setHasActiveEdits] = useState(false);
  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });
  const [fileData, setFileData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [baseDefaultColumns, setBaseDefaultColumns] = useState([]);
  const [isAddRecordFormOpen, setIsAddRecordFormOpen] = useState(false);
  const [isEditRecordFormOpen, setIsEditRecordFormOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const clearData = () => {};

  const handleClose = () => {
    setIsCsvPreviewOpen(false);
    setFileData([]);
  };

  const handleCancel = () => {
    clearData();
    handleClose();
  };

  const toCamelCase = str =>
    str
      .replace(/\s(.)/g, $1 => $1.toUpperCase())
      .replace(/\s/g, '')
      .replace(/^(.)/, $1 => $1.toLowerCase());

  const convertKeysOfObjectInCamelCase = object =>
    Object.entries(object).reduce((carry, [key, value]) => {
      // eslint-disable-next-line no-param-reassign
      carry[toCamelCase(key)] = value;
      return carry;
    }, {});

  const convertFileData = data =>
    data.map(record => convertKeysOfObjectInCamelCase(record));

  const toFindDuplicates = array =>
    array.filter((item, index) => array.indexOf(item) !== index);

  const isRecordValid = (fieldAndValidationFunctions, record, data) => {
    const isRecordValidList = fieldAndValidationFunctions.map(item => {
      if (item?.isUnique) {
        const arrayOfFields = data.map(obj => obj[`${item.title}`]);
        const duplicateFields = toFindDuplicates(arrayOfFields);
        const isDuplicate =
          duplicateFields.includes(record?.[`${item.title}`]) ||
          arrayOfFields.includes(record?.[`${item.title}`]);
        return !isDuplicate;
      }
      if (item?.validationFunction(record[`${item.title}`])) {
        return true;
      }
      return false;
    });
    if (isRecordValidList.some(val => val === false)) {
      return false;
    }
    return true;
  };

  const validateFileData = (data, fieldAndValidValues, currentFileData) => {
    const columnsThatShouldBeUnique = fieldAndValidValues.filter(
      field => field.isUnique,
    );
    const valuesOfColumnsThatShouldBeUnique = columnsThatShouldBeUnique.map(
      col => data.map(obj => obj[`${col.title}`]),
    );
    const mergedValuesOfColumnsThatShouldBeUnique = [].concat(
      ...valuesOfColumnsThatShouldBeUnique,
    );
    const duplicateValues = toFindDuplicates(
      mergedValuesOfColumnsThatShouldBeUnique,
    );
    return data.map(record => {
      const valuesOfRecord = Object.values(record);
      const areValuesDuplicate = valuesOfRecord.map(value =>
        duplicateValues.includes(value),
      );
      if (
        isRecordValid(fieldAndValidValues, record, currentFileData) &&
        areValuesDuplicate.every(val => val === false)
      ) {
        return { ...record, Error: false };
      }
      return { ...record, Error: true };
    });
  };
  const runPreSubmitChecks = () => {
    return true;
  };

  const validateHeaders = (data, validHeaders) => {
    const headersArrays = data.map(record => Object.keys(record));
    const mergedHeaders = [].concat(...headersArrays);
    const uniqueHeadersSet = new Set(mergedHeaders);
    const uniqueHeaders = [...uniqueHeadersSet];
    let areHeadersValid;
    validHeaders.forEach(validHeader => {
      if (!uniqueHeaders.includes(validHeader)) {
        setIsCsvPreviewOpen(false);
        showError({
          contextText: MESSAGE.INVALID_HEADERS,
        });
        areHeadersValid = false;
      }
      areHeadersValid = true;
    });
    return areHeadersValid;
  };

  const convertCamelToNormalCase = text => {
    const result = text.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
  };

  const removeRecord = () => {
    const rowsToBeDeleted = selectedRows.map(row => {
      // eslint-disable-next-line no-param-reassign
      delete row.isSelected;
      return row;
    });
    const rowsToBeDeletedInStringFormat = rowsToBeDeleted.map(record =>
      JSON.stringify(record),
    );
    const fileDataAfterRowDeletion = fileData.filter(
      record => !rowsToBeDeletedInStringFormat.includes(JSON.stringify(record)),
    );
    setFileData(fileDataAfterRowDeletion);
  };

  const addRecordToFileData = record => {
    let lastRecord;
    let idOfLastRecord;
    if (fileData.length > 0) {
      lastRecord = fileData.slice(-1);
      idOfLastRecord = lastRecord[0].id;
    } else {
      idOfLastRecord = 0;
    }
    if (isRecordValid(headerConfiguration, record, fileData)) {
      setFileData(prev => [
        ...prev,
        { ...record, Error: false, id: idOfLastRecord + 1 },
      ]);
    } else {
      setFileData(prev => [
        ...prev,
        { ...record, Error: true, id: idOfLastRecord + 1 },
      ]);
    }
  };

  const editRecordFromFileData = record => {
    const idOfGivenRecord = selectedRows[0].id;
    const dataWithoutGivenRecord = fileData.filter(
      item => item.id !== idOfGivenRecord,
    );
    if (isRecordValid(headerConfiguration, record, dataWithoutGivenRecord)) {
      const temporaryData = fileData;
      const dataAfterEdit = temporaryData.map(object => {
        if (object.id === idOfGivenRecord) {
          return { ...record, id: object.id, Error: false };
        }
        return object;
      });
      setFileData(dataAfterEdit);
    } else {
      const temporaryData = fileData;
      const dataAfterEdit = temporaryData.map(object => {
        if (object.id === idOfGivenRecord) {
          return { ...record, Error: true };
        }
        return object;
      });
      setFileData(dataAfterEdit);
    }
  };

  const editRecord = record => {
    setIsEditRecordFormOpen(true);
    const keysOfRecord = Object.keys(record);
    requiredParams.forEach(item => {
      keysOfRecord.forEach(key => {
        if (toCamelCase(item.name) === key) {
          item.setValueFunction(record[key]);
          item.validationFunction(record[key]);
        }
        if (toCamelCase(item.name) === key && item.requiredOptions) {
          item.setValueFunction({
            value: record[key],
            label: record[key],
          });
          item.validationFunction(record[key]);
        }
      });
    });
  };

  const convertFileDataToJsonFormat = (data, headerAndJsonFields) => {
    const jsonFormattedData = [];
    data.forEach(record => {
      const object = record;
      // eslint-disable-next-line no-unused-expressions
      headerAndJsonFields.forEach(headerAndJsonFieldObj => {
        const camelCaseHeader = toCamelCase(
          headerAndJsonFieldObj.csvHeaderName,
        );
        if (Object.keys(record).includes(camelCaseHeader)) {
          // eslint-disable-next-line no-param-reassign
          object[`${headerAndJsonFieldObj.jsonFieldName}`] =
            object[`${camelCaseHeader}`];
          if (
            headerAndJsonFieldObj.jsonFieldName !==
            toCamelCase(headerAndJsonFieldObj.csvHeaderName)
          ) {
            delete object[`${camelCaseHeader}`];
          }
          delete object.Error;
        }
      });
      jsonFormattedData.push(object);
    });
    return jsonFormattedData;
  };

  const checkForDuplicateRows = data => {
    const dataWithoutId = data.map(record => {
      // eslint-disable-next-line no-param-reassign
      delete record.id;
      return record;
    });
    const dataInStringFormat = dataWithoutId.map(record =>
      JSON.stringify(record),
    );
    const duplicateRows = toFindDuplicates(dataInStringFormat);
    if (duplicateRows.length) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (validCsvHeaders) {
      const headersInGivenOrder = validCsvHeaders.map(header =>
        toCamelCase(header),
      );
      setHeaders([...headersInGivenOrder, 'Error']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validCsvHeaders]);

  useEffect(() => {
    setBaseDefaultColumns(
      headers?.map(header =>
        header === 'Error'
          ? {
              width: 160,
              title: header,
              field: header,
              enableSearch: true,
              enableFilter: true,
              // eslint-disable-next-line react/prop-types
              render: row => (
                <div style={{ textAlign: 'center' }}>
                  {row.Error === true ? <InfoIconWithPopover /> : null}
                </div>
              ),
            }
          : header !== 'id' && {
              width: 180,
              title: convertCamelToNormalCase(header),
              field: header,
              enableSearch: true,
              enableFilter: true,
              // eslint-disable-next-line react/prop-types
              render: val => (
                <div style={{ textAlign: 'center' }}>
                  <div className={clsx(val?.Error && styles.errorRow)}>
                    {val[`${header}`]}
                  </div>
                </div>
              ),
            },
      ),
    );
  }, [headers]);

  useEffect(() => {
    setRowsData({
      data: fileData,
      filteredData: fileData,
      loading: false,
    });
  }, [fileData]);

  const getSelectedRows = rows => {
    setSelectedRows(rows);
  };

  useEffect(() => {
    if (csvFileData?.length) {
      if (validateHeaders(csvFileData, validCsvHeaders)) {
        const convertedFileData = convertFileData(csvFileData);
        const validatedFileData = validateFileData(
          convertedFileData,
          headerConfiguration,
          [],
        );
        const validFileDataWithId = validatedFileData.map((record, index) => ({
          ...record,
          id: index,
        }));
        setFileData(validFileDataWithId);
      } else {
        setFileData([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvFileData]);

  useEffect(() => {
    if (!open) {
      setFileData([]);
    }
  }, [open]);

  return (
    <Modal open={open} onClose={handleCancel} className={classes.modal}>
      <div className={styles.formContainer}>
        <div className={styles.backButtonAndFormNameWrapper}>
          <BackButton
            action={backButtonName}
            handleClick={handleClose}
            isActionFontWightNormal
          />
          <div className={styles.name}>{formName}</div>
        </div>

        <div
          style={{
            padding: '1em',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
          }}
        >
          <div className={styles.tableContainer}>
            {baseDefaultColumns.length && (
              <CsvPreviewTable
                columns={[...baseDefaultColumns]}
                defaultColumns={[...baseDefaultColumns]}
                rowHeight={ROW_HEIGHT}
                rows={rowsData.data}
                setHasActiveEdits={setHasActiveEdits}
                setQuantity={() => true}
                isDataLoading={rowsData.loading}
                isActionsBarAbsent
                primaryAction
                getSelectedRows={getSelectedRows}
              />
            )}
          </div>
          <div>
            <span className={styles.inputTagWrapper}>
              <input
                className={styles.input}
                type="file"
                id="upload-csv"
                accept=".csv"
                onClick={e => {
                  e.stopPropagation();
                }}
                onChange={e => {
                  const elem = e.target;
                  setFileData([]);
                  readExcel(elem.files[0], setCsvFileData, csvDataFormatter);
                  setIsCsvPreviewOpen(true);
                  elem.value = ''; // reset input element
                }}
              />
              <Button className={styles.reUploadButton}>Re-Upload</Button>
            </span>
            <Button
              className={styles.addRecordButton}
              onClick={() => {
                requiredParams.map(record => record?.setHighlightField(false));
                setIsAddRecordFormOpen(true);
              }}
            >
              ADD
            </Button>
            <Button
              className={styles.addRecordButton}
              disabled={selectedRows.length !== 1}
              variant="primary"
              onClick={() => editRecord(selectedRows[0])}
            >
              EDIT
            </Button>
            {isEditRecordFormOpen && (
              <AddNewRowToCsvPreviewForm
                formName={editRowFormName}
                open={isEditRecordFormOpen}
                handleClose={() => setIsEditRecordFormOpen(false)}
                setRecord={editRecordFromFileData}
                requiredParams={requiredParams}
                message={
                  editRowSuccessMessage || MESSAGE.PARAMETER_EDIT_SUCCESSFUL
                }
                action={ACTIONS.UPDATE}
              />
            )}
            <Button
              className={styles.removeRecordButton}
              disabled={!selectedRows.length}
              variant="primary"
              onClick={() => removeRecord()}
            >
              REMOVE
            </Button>
            {isAddRecordFormOpen && (
              <AddNewRowToCsvPreviewForm
                formName={addNewRowFormName}
                open={isAddRecordFormOpen}
                handleClose={() => setIsAddRecordFormOpen(false)}
                setRecord={addRecordToFileData}
                requiredParams={requiredParams}
                message={
                  addNewRowSuccessMessage || MESSAGE.PARAMETER_ADD_SUCCESSFUL
                }
                action={ACTIONS.ADD}
              />
            )}
            <Button
              className={styles.submitButton}
              onClick={() => {
                if (runPreSubmitChecks()) {
                  if (checkForDuplicateRows(fileData)) {
                    showError({
                      contextText: 'File contains duplicate rows',
                    });
                  }
                  const jsonFormattedData = convertFileDataToJsonFormat(
                    fileData,
                    headersWithJsonFields,
                  );
                  handleSubmit(jsonFormattedData);
                  handleClose();
                }
              }}
              disabled={
                fileData.some(record => record.Error === true) ||
                fileData?.length <= 0
              }
              aria-label="submit"
            >
              Submit
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

CsvPreview.propTypes = {
  open: PropTypes.bool.isRequired,
  setIsCsvPreviewOpen: PropTypes.func.isRequired,
  csvFileData: PropTypes.array.isRequired,
  setCsvFileData: PropTypes.func.isRequired,
  backButtonName: PropTypes.string.isRequired,
  formName: PropTypes.string.isRequired,
  csvDataFormatter: PropTypes.func,
  handleSubmit: PropTypes.func.isRequired,
  headerConfiguration: PropTypes.array.isRequired,
  headersWithJsonFields: PropTypes.array.isRequired,
  requiredParams: PropTypes,
  validCsvHeaders: PropTypes.arrayOf(PropTypes.string),
  addNewRowFormName: PropTypes.string.isRequired,
  editRowFormName: PropTypes.array.isRequired,
  addNewRowSuccessMessage: PropTypes.string,
  editRowSuccessMessage: PropTypes.string,
};

export default CsvPreview;
